import { useState, useEffect, useRef } from 'react';
import { Phone, X, PhoneOff, User, Mic, MicOff, Video, VideoOff } from 'lucide-react';
import { toast } from 'sonner';
import { useWebRTC } from './WebRTCProvider';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { useAuthContext } from '../contexts/AuthContext';
import { logger } from '../utils/logger';

type CallState = 'idle' | 'incoming' | 'outgoing' | 'connecting' | 'active' | 'ended';

interface IncomingCallData {
  from: string;
  fromName: string;
  fromPhoto?: string;
  roomId: string;
  isVideoCall: boolean;
  toName?: string;
  toPhoto?: string;
}

export const IncomingCallNotification = () => {
  const { webrtcService } = useWebRTC();
  const { user } = useAuthContext();
  const [callData, setCallData] = useState<IncomingCallData | null>(null);
  const [callState, setCallState] = useState<CallState>('idle');
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isCallDelivered, setIsCallDelivered] = useState(false); // Track if recipient saw the call
  
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const callTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Log component mount
  useEffect(() => {
    console.log('%c🔔 IncomingCallNotification Component Mounted', 'font-size: 14px; font-weight: bold; color: #00aa00; background: #f0f0f0; padding: 4px 8px;');
    console.log('✅ Ready to receive video call notifications');
    console.log('💡 Test manually: __testVideoCall() in console');
    
    return () => {
      console.log('%c🔔 IncomingCallNotification Component Unmounted', 'font-size: 14px; font-weight: bold; color: #ff6600;');
    };
  }, []);

  // Debug: Track callState changes
  useEffect(() => {
    logger.log('🔔 [IncomingCallNotification] Call state changed to:', callState);
    if (callState === 'incoming' && callData) {
      logger.log('🔔 [IncomingCallNotification] ✅ INCOMING CALL - Modal should be VISIBLE now!');
      logger.log('   Call data:', callData);
    }
  }, [callState, callData]);

  // Register incoming call callback
  useEffect(() => {
    logger.log('🔔 [IncomingCallNotification] Component mounted - registering all callbacks');
    logger.log('🔔 [IncomingCallNotification] Ready to receive calls');
    
    webrtcService.onIncomingCall((signal) => {
      logger.log('🔔🔥 [IncomingCallNotification] INCOMING CALL RECEIVED:', signal);
      
      // 🔥 PREVENT MULTIPLE CALLS: Check if already in a call
      if (callState !== 'idle') {
        logger.log('❌ [IncomingCallNotification] Already in a call, rejecting new incoming call');
        // Automatically reject the call
        webrtcService.rejectCall(signal.roomId || '', signal.from || '', 'Already in another call');
        toast.error('Call rejected - already in another call');
        return;
      }
      
      setCallData({
        from: signal.from || '',
        fromName: signal.fromName || 'Unknown',
        fromPhoto: signal.fromPhoto,
        roomId: signal.roomId || '',
        isVideoCall: signal.isVideoCall || false,
        toName: signal.toName,
        toPhoto: signal.toPhoto
      });
      setCallState('incoming');
      
      // ✅ Notify sender that call is delivered/ringing
      webrtcService.notifyCallDelivered(signal.roomId || '', signal.from || '');
      
      // Play ringtone
      if (audioRef.current) {
        audioRef.current.play().catch(err => {
          logger.log('Could not play ringtone:', err);
        });
      }
      
      toast.info(`Incoming ${signal.isVideoCall ? 'video' : 'audio'} call from ${signal.fromName}`);
    });
    
    // Listen for video call notifications from Expo app (when notification is tapped)
    const handleVideoCallFromNotification = (event: any) => {
      logger.log('🔔📞 [NOTIFICATION EVENT] Received incomingVideoCallFromNotification event!');
      logger.log('🔔📞 [NOTIFICATION EVENT] Event detail:', JSON.stringify(event.detail, null, 2));
      
      const { roomId, from, fromName, fromPhoto, isVideoCall } = event.detail;
      
      // 🔥 PREVENT MULTIPLE CALLS: Check if already in a call
      if (callState !== 'idle') {
        logger.log('❌ [NOTIFICATION EVENT] Already in a call, rejecting new incoming call');
        // Automatically reject the call
        webrtcService.rejectCall(roomId || '', from || '', 'Already in another call');
        toast.error('Call rejected - already in another call');
        return;
      }
      
      logger.log('🔔📞 [NOTIFICATION EVENT] Setting call state to incoming...');
      logger.log('   roomId:', roomId);
      logger.log('   from:', from);
      logger.log('   fromName:', fromName);
      logger.log('   isVideoCall:', isVideoCall);
      
      // Show the incoming call modal with the data from notification
      setCallData({
        from: from || '',
        fromName: fromName || 'Unknown',
        fromPhoto: fromPhoto,
        roomId: roomId || '',
        isVideoCall: isVideoCall !== false,
        toName: user?.name,
        toPhoto: user?.photos?.[0]?.url
      });
      setCallState('incoming');
      
      // ✅ Notify sender that call is delivered/ringing
      webrtcService.notifyCallDelivered(roomId || '', from || '');
      
      logger.log('✅ [NOTIFICATION EVENT] Call state set to incoming - modal should be visible now!');
      
      // Play ringtone
      if (audioRef.current) {
        audioRef.current.play().catch(err => {
          logger.log('Could not play ringtone:', err);
        });
      }
      
      toast.info(`Incoming ${isVideoCall ? 'video' : 'audio'} call from ${fromName}`);
    };
    
    logger.log('🔔📞 [IncomingCallNotification] Adding event listener for incomingVideoCallFromNotification');
    window.addEventListener('incomingVideoCallFromNotification', handleVideoCallFromNotification);
    logger.log('✅ [IncomingCallNotification] Event listener registered successfully');

    webrtcService.onOutgoingCall((signal) => {
      logger.log('📞🔥 [IncomingCallNotification] OUTGOING CALL INITIATED:', signal);
      
      setCallData({
        from: signal.from || '',
        fromName: signal.fromName || '',
        fromPhoto: signal.fromPhoto,
        roomId: signal.roomId || '',
        isVideoCall: signal.isVideoCall || false,
        toName: signal.toName || 'Unknown',
        toPhoto: signal.toPhoto
      });
      setCallState('outgoing');
      setIsCallDelivered(false); // Reset call delivered state for new outgoing call
      
      // 🔥 FIX: Clear any existing timeout
      if (callTimeoutRef.current) {
        clearTimeout(callTimeoutRef.current);
      }
      
      // 🔥 FIX: Automatically end call after 60 seconds if not answered
      callTimeoutRef.current = setTimeout(() => {
        logger.log('⏰ [IncomingCallNotification] Call timeout - checking state...');
        setCallState(current => {
          logger.log('⏰ [IncomingCallNotification] Current state:', current);
          if (current === 'outgoing') {
            logger.log('⏰ [IncomingCallNotification] Call not answered after 60s - ending');
            handleEndCall();
            toast.error('Call was not answered');
          }
          return current;
        });
      }, 60000);
    });

    webrtcService.onCallAccepted(async () => {
      logger.log('✅ [IncomingCallNotification] Call accepted by remote peer');
      
      // 🔥 FIX: Clear call timeout since call was accepted
      if (callTimeoutRef.current) {
        clearTimeout(callTimeoutRef.current);
        callTimeoutRef.current = null;
      }
      
      const currentCallData = callData || {
        roomId: webrtcService.getRoomId(),
        isVideoCall: webrtcService.getIsVideoCall()
      };
      
      setCallState('connecting');
      
      if (!currentCallData.roomId) {
        console.error('❌ No roomId available');
        return;
      }
      
      try {
        const peerConnection = await webrtcService.initializePeerConnection(currentCallData.roomId, currentCallData.isVideoCall);
        
        const stream = webrtcService.getLocalStream();
        if (stream && localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
          setLocalStream(stream);
          logger.log('✅ [Caller] Local stream set:', {
            videoTracks: stream.getVideoTracks().length,
            audioTracks: stream.getAudioTracks().length
          });
        }
        
        await webrtcService.createOffer();
        
        const checkRemoteStream = setInterval(() => {
          const remote = webrtcService.remoteStream;
          if (remote && remoteVideoRef.current) {
            logger.log('✅ [Caller] Remote stream received:', {
              videoTracks: remote.getVideoTracks().length,
              audioTracks: remote.getAudioTracks().length
            });
            remoteVideoRef.current.srcObject = remote;
            setRemoteStream(remote);
            setCallState('active');
            clearInterval(checkRemoteStream);
          }
        }, 500);
        
        setTimeout(() => clearInterval(checkRemoteStream), 30000);
      } catch (error: any) {
        console.error('❌ Error initializing caller peer connection:', error);
        toast.error(error.message || 'Failed to connect call');
        handleEndCall();
      }
    });

    webrtcService.onCallRejected((reason) => {
      logger.log('❌ [IncomingCallNotification] Call rejected:', reason);
      
      // 🔥 FIX: Clear call timeout
      if (callTimeoutRef.current) {
        clearTimeout(callTimeoutRef.current);
        callTimeoutRef.current = null;
      }
      
      handleEndCall();
      toast.error(reason || 'Call was rejected');
    });

    webrtcService.onCallEnded(() => {
      logger.log('👋 [IncomingCallNotification] Call ended');
      
      // 🔥 FIX: Clear call timeout
      if (callTimeoutRef.current) {
        clearTimeout(callTimeoutRef.current);
        callTimeoutRef.current = null;
      }
      
      handleEndCall();
    });

    webrtcService.onCallDelivered(() => {
      logger.log('✅ [IncomingCallNotification] Call delivered - recipient is viewing the call');
      setIsCallDelivered(true);
    });

    return () => {
      logger.log('🔔 [IncomingCallNotification] Cleanup - unregistering callbacks');
      window.removeEventListener('incomingVideoCallFromNotification', handleVideoCallFromNotification);
      
      // Clear call timeout on unmount
      if (callTimeoutRef.current) {
        clearTimeout(callTimeoutRef.current);
        callTimeoutRef.current = null;
      }
    };
  }, [webrtcService]); // Only depend on webrtcService - don't re-register when callData/user changes

  const handleAcceptCall = async () => {
    if (!callData) return;
    
    logger.log('✅ [IncomingCallNotification] Accepting call');
    logger.log('✅ [IncomingCallNotification] User ID:', user?._id);
    
    // Stop ringtone
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    
    setCallState('connecting');
    
    try {
      // Initialize peer connection and get local stream - pass userId from context AND caller's ID
      await webrtcService.acceptCall(callData.roomId, user?._id, callData.from);
      const peerConnection = await webrtcService.initializePeerConnection(callData.roomId, callData.isVideoCall);
      
      // Get local stream
      const stream = webrtcService.getLocalStream();
      if (stream && localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
        setLocalStream(stream);
        logger.log('✅ [Receiver] Local stream set:', {
          videoTracks: stream.getVideoTracks().length,
          audioTracks: stream.getAudioTracks().length
        });
      }
      
      // Listen for remote stream
      const checkRemoteStream = setInterval(() => {
        const remote = webrtcService.remoteStream;
        if (remote && remoteVideoRef.current) {
          logger.log('✅ [Receiver] Remote stream received:', {
            videoTracks: remote.getVideoTracks().length,
            audioTracks: remote.getAudioTracks().length
          });
          remoteVideoRef.current.srcObject = remote;
          setRemoteStream(remote);
          setCallState('active');
          clearInterval(checkRemoteStream);
        }
      }, 500);
      
      // Clear interval after 30 seconds
      setTimeout(() => clearInterval(checkRemoteStream), 30000);
      
      toast.success('Call connected');
    } catch (error: any) {
      console.error('❌ [IncomingCallNotification] Error accepting call:', error);
      toast.error(error.message || 'Failed to accept call');
      handleEndCall();
    }
  };

  const handleRejectCall = () => {
    if (!callData) return;
    
    logger.log('❌ [IncomingCallNotification] Rejecting call');
    
    // Stop ringtone
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    
    webrtcService.rejectCall(callData.roomId);
    handleEndCall();
    toast.info('Call rejected');
  };

  const handleEndCall = () => {
    logger.log('👋 [IncomingCallNotification] Ending call');
    logger.log('👋 [IncomingCallNotification] Call state before end:', callState);
    
    // 🔥 FIX: Clear call timeout if exists
    if (callTimeoutRef.current) {
      clearTimeout(callTimeoutRef.current);
      callTimeoutRef.current = null;
    }
    
    // Stop ringtone
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    
    // 🔥 FIX: Only send end signal if we're in an active or connecting state
    // Don't send if we're just in outgoing state with no response
    if (callState === 'active' || callState === 'connecting' || callState === 'incoming') {
      webrtcService.endCall();
    } else if (callState === 'outgoing') {
      // For outgoing calls that were never answered, just disconnect without sending end signal
      logger.log('👋 [IncomingCallNotification] Outgoing call not answered - canceling');
      webrtcService.endCall(); // This will clean up the call
    }
    
    // Clean up streams
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
    }
    
    setCallData(null);
    setCallState('idle');
    setLocalStream(null);
    setRemoteStream(null);
    setIsMuted(false);
    setIsVideoEnabled(true);
    setIsCallDelivered(false); // Reset call delivered state
  };

  const toggleMute = () => {
    logger.log('🎤 [toggleMute] Button clicked');
    
    // Get the stream from the webrtcService instead of component state
    const stream = webrtcService.getLocalStream();
    logger.log('🎤 [toggleMute] Stream from service:', stream);
    logger.log('🎤 [toggleMute] isMuted:', isMuted);
    
    if (stream) {
      const audioTracks = stream.getAudioTracks();
      logger.log('🎤 [toggleMute] Audio tracks:', audioTracks.length);
      
      const newMutedState = !isMuted;
      audioTracks.forEach((track, index) => {
        logger.log(`🎤 [toggleMute] Track ${index} before:`, track.enabled);
        track.enabled = !newMutedState;
        logger.log(`🎤 [toggleMute] Track ${index} after:`, track.enabled);
      });
      setIsMuted(newMutedState);
      logger.log('🎤 Audio toggled:', newMutedState ? 'MUTED' : 'UNMUTED');
    } else {
      console.error('❌ [toggleMute] No localStream available from webrtcService!');
    }
  };

  const toggleVideo = async () => {
    logger.log('📹 [toggleVideo] Button clicked');
    
    // Get the stream from the webrtcService instead of component state
    const stream = webrtcService.getLocalStream();
    logger.log('📹 [toggleVideo] Stream from service:', stream);
    logger.log('📹 [toggleVideo] isVideoEnabled:', isVideoEnabled);
    
    if (stream) {
      const videoTracks = stream.getVideoTracks();
      logger.log('📹 [toggleVideo] Video tracks:', videoTracks.length);
      
      if (videoTracks.length === 0) {
        console.error('❌ [toggleVideo] No video tracks found!');
        return;
      }

      const newVideoState = !isVideoEnabled;
      
      // Check if tracks are still live
      const allTracksLive = videoTracks.every(track => track.readyState === 'live');
      
      if (!allTracksLive) {
        logger.warn('⚠️ [toggleVideo] Some tracks are not live, attempting to get new stream');
        
        try {
          // Get the old stream to preserve audio track
          const oldStream = webrtcService.getLocalStream();
          const oldAudioTrack = oldStream?.getAudioTracks()[0];
          
          // Get a new stream with video only
          const newVideoStream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: false // We'll keep the existing audio track
          });
          
          // Replace the video track in peer connection
          const newVideoTrack = newVideoStream.getVideoTracks()[0];
          const peerConnection = webrtcService.getPeerConnection();
          
          if (peerConnection) {
            const sender = peerConnection.getSenders().find(s => s.track?.kind === 'video');
            if (sender) {
              await sender.replaceTrack(newVideoTrack);
              logger.log('✅ [toggleVideo] Video track replaced successfully');
            }
          }
          
          // Create a new MediaStream with the new video track and old audio track
          const combinedStream = new MediaStream();
          combinedStream.addTrack(newVideoTrack);
          if (oldAudioTrack) {
            combinedStream.addTrack(oldAudioTrack);
            logger.log('✅ [toggleVideo] Audio track preserved');
          }
          
          // Update local stream in webrtcService
          webrtcService.setLocalStream(combinedStream);
          
          // Update local video element
          if (localVideoRef.current) {
            localVideoRef.current.srcObject = combinedStream;
          }
          
          setIsVideoEnabled(true);
          logger.log('✅ [toggleVideo] New video stream acquired and enabled');
        } catch (error) {
          console.error('❌ [toggleVideo] Failed to get new video stream:', error);
          toast.error('Failed to enable video. Please check camera permissions.');
          return;
        }
      } else {
        // Tracks are live, just toggle enabled state
        videoTracks.forEach((track, index) => {
          logger.log(`📹 [toggleVideo] Track ${index} readyState:`, track.readyState);
          logger.log(`📹 [toggleVideo] Track ${index} before:`, track.enabled);
          track.enabled = newVideoState;
          logger.log(`📹 [toggleVideo] Track ${index} after:`, track.enabled);
        });
        setIsVideoEnabled(newVideoState);
        logger.log('📹 Video toggled:', newVideoState ? 'ON' : 'OFF');
      }
    } else {
      console.error('❌ [toggleVideo] No localStream available from webrtcService!');
    }
  };

  if (callState === 'idle') {
    return (
      <>
        {/* Hidden audio element for ringtone */}
        <audio ref={audioRef} loop>
          <source src="data:audio/wav;base64,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" type="audio/wav" />
        </audio>
      </>
    );
  }

  // Incoming call notification (ringing state)
  if (callState === 'incoming' && callData) {
    return (
      <>
        {/* Hidden audio element for ringtone */}
        <audio ref={audioRef} loop autoPlay>
          <source src="data:audio/wav;base64,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" type="audio/wav" />
        </audio>
        
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[9999] p-4">
          <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-3xl p-8 max-w-md w-full shadow-2xl text-white">
            <div className="text-center mb-8">
              <div className="mb-6 relative inline-block">
                {callData.fromPhoto ? (
                  <ImageWithFallback
                    src={callData.fromPhoto}
                    alt={callData.fromName}
                    className="w-32 h-32 rounded-full object-cover ring-4 ring-white/30"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full bg-white/20 flex items-center justify-center ring-4 ring-white/30">
                    <User className="h-16 w-16 text-white" />
                  </div>
                )}
                <div className="absolute -bottom-2 -right-2 bg-white rounded-full p-3 shadow-lg">
                  {callData.isVideoCall ? (
                    <Video className="h-6 w-6 text-rose-500" />
                  ) : (
                    <Phone className="h-6 w-6 text-rose-500" />
                  )}
                </div>
              </div>
              
              <h3 className="text-3xl font-bold mb-2">{callData.fromName}</h3>
              <p className="text-white/90 text-lg animate-pulse">
                Incoming {callData.isVideoCall ? 'video' : 'audio'} call...
              </p>
            </div>

            <div className="flex gap-4 justify-center">
              <button
                onClick={handleRejectCall}
                className="bg-red-500 hover:bg-red-600 text-white rounded-full p-6 shadow-lg transition-all hover:scale-110"
              >
                <PhoneOff className="h-8 w-8" />
              </button>
              
              <button
                onClick={handleAcceptCall}
                className="bg-green-500 hover:bg-green-600 text-white rounded-full p-6 shadow-lg transition-all hover:scale-110 animate-bounce"
              >
                <Phone className="h-8 w-8" />
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Outgoing call UI (waiting for receiver to accept)
  if (callState === 'outgoing' && callData) {
    return (
      <>
        <audio ref={audioRef} loop>
          <source src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZSA0PVKzn77BdGAg+ltrzxnMpBSl+zPLaizsIGGS57OihUBELTKXh8bllHAU2jtXzzn0vBSF1xe/glEILElyx6O2nVRUJQ5zd8sFuJAUuhM/z1YU2Bhxqvu7mnEoODlOq5vKsXBoHPJPY88p2KwUme8rx3I4+CRZiturqpFITC0mi4PK9aiAFMYnU8tGAMQYfcsLu45dFDBFbr+ftrWAdCDuP1vPJdSsFKH3K8dvbPQgZaLvs56JQEg1Lpd/yvWceBS+F0fLPfzAFImG+7+CXRg0SV63n7q5hHQc8kNbzyXQrBSp9y/HblUAKF2G+7OalUBELTKTg8r1oHQU0idT0z4AxBiNwwu7ilUINE1mt5+6tYR0HPJDW88l0KwUqfcvx25hBChthvu3mpVASC0yk4PK9aB0FNInU9M+AMQYjccLu4pVCDBNarefurWEdBzyQ1vPJdCsFKn3L8duYQQobYb7t5qVQEgtMpODyvWgdBTSJ1PTPgDEGI3HC7uKVQgwTWq3n7q1hHQc8kNbzyXQrBSp9y/HbmEEKG2G+7ealUBILTKTg8r1oHQU0idT0z4AxBiNxwu7ilUIME1qt5+6tYR0HPJDW88l0KwUqfcvx25hBChthvu3mpVASC0yk4PK9aB0FNInU9M+AMQYjccLu4pVCDBNarefurWEdBzyQ1vPJdCsFKn3L8duYQQobYb7t5qVQEgtMpODyvWgdBTSJ1PTPgDEGI3HC7uKVQgwTWq3n7q1hHQc8kNbzyXQrBSp9y/HbmEEKG2G+7ealUBILTKTg8r1oHQU0idT0z4AxBiNxwu7ilUIME1qt5+6tYR0HPJDW88l0KwUqfcvx25hBChthvu3mpVASC0yk4PK9aB0FNInU9M+AMQYjccLu4pVCDBNarefurWEdBzyQ1vPJdCsFKn3L8duYQQobYb7t5qVQEgtMpODyvWgdBTSJ1PTPgDEGI3HC7uKVQgwTWq3n7q1hHQc8kNbzyXQrBSp9y/HbmEEKG2G+7ealUBILTKTg8r1oHQU0idT0z4AxBiNxwu7ilUIME1qt5+6tYR0HPJDW88l0KwUqfcvx25hBChthvu3mpVASC0yk4PK9aB0FNInU9M+AMQYjccLu4pVCDBNarefurWEdBzyQ1vPJdCsFKn3L8duYQQobYb7t5qVQEgtMpODyvWgdBTSJ1PTPgDEGI3HC7uKVQgwTWq3n7q1hHQc8kNbzyXQrBSp9y/HbmEEKG2G+7ealUBILTKTg8r1oHQU0idT0z4AxBiNxwu7ilUIME1qt5+6tYR0HPJDW88l0KwUqfcvx25hBChthvu3mpVASC0yk4PK9aB0FNInU9M+AMQYjccLu4pVCDBNarefurWEdBzyQ1vPJdCsFKn3L8duYQQobYb7t5qVQEgtMpODyvWgdBTSJ1PTPgDEGI3HC7uKVQgwTWq3n7q1hHQc8kNbzyXQrBSp9y/HbmEEKG2G+7ealUBILTKTg8r1oHQU0idT0z4AxBiNxwu7ilUIME1qt5+6tYR0HPJDW88l0KwUqfcvx25hBChthvu3mpVASC0yk4PK9aB0FNInU9M+AMQYjccLu4pVCDBNarefurWEdBzyQ1vPJdCsFKn3L8duYQQobYb7t5qVQEgtMpODyvWgdBTSJ1PTPgDEGI3HC7uKVQgwTWq3n7q1hHQ==" type="audio/wav" />
        </audio>
        
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[9999] p-4">
          <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-3xl p-8 max-w-md w-full shadow-2xl text-white">
            <div className="text-center mb-8">
              <div className="mb-6 relative inline-block">
                {callData.toPhoto ? (
                  <ImageWithFallback
                    src={callData.toPhoto}
                    alt={callData.toName || 'User'}
                    className="w-32 h-32 rounded-full object-cover ring-4 ring-white/30"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full bg-white/20 flex items-center justify-center ring-4 ring-white/30">
                    <User className="h-16 w-16 text-white" />
                  </div>
                )}
                <div className="absolute -bottom-2 -right-2 bg-white rounded-full p-3 shadow-lg animate-pulse">
                  {callData.isVideoCall ? (
                    <Video className="h-6 w-6 text-rose-500" />
                  ) : (
                    <Phone className="h-6 w-6 text-rose-500" />
                  )}
                </div>
              </div>
              
              <h3 className="text-3xl font-bold mb-2">{callData.toName || 'User'}</h3>
              <p className="text-white/90 text-lg animate-pulse">
                {isCallDelivered ? 'Ringing...' : 'Calling...'}
              </p>
            </div>

            <div className="flex justify-center">
              <button
                onClick={handleEndCall}
                className="bg-red-500 hover:bg-red-600 text-white rounded-full p-6 shadow-lg transition-all hover:scale-110"
              >
                <PhoneOff className="h-8 w-8" />
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Active call UI
  if ((callState === 'connecting' || callState === 'active') && callData) {
    // Get userId from localStorage user object
    let userId;
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        userId = user._id;
      }
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
    }
    
    const isReceiver = callData.from !== userId;
    const otherPersonName = isReceiver ? callData.fromName : callData.toName;
    const otherPersonPhoto = isReceiver ? callData.fromPhoto : callData.toPhoto;
    
    return (
      <div className="fixed inset-0 bg-gradient-to-b from-gray-900 to-black z-[9999] flex flex-col">
        {/* Video containers */}
        <div className="flex-1 relative bg-black">
          {/* Remote video (full screen) */}
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={{ display: remoteStream ? 'block' : 'none' }}
          />
          
          {/* No video placeholder */}
          {!remoteStream && (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
              <div className="text-center text-white">
                {otherPersonPhoto ? (
                  <ImageWithFallback
                    src={otherPersonPhoto}
                    alt={otherPersonName || 'User'}
                    className="w-40 h-40 rounded-full object-cover mx-auto mb-6 ring-4 ring-rose-500/30 shadow-2xl"
                  />
                ) : (
                  <div className="w-40 h-40 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center mx-auto mb-6 ring-4 ring-rose-500/30 shadow-2xl">
                    <User className="h-20 w-20 text-white" />
                  </div>
                )}
                <p className="text-2xl font-semibold mb-2">{otherPersonName || 'User'}</p>
                <p className="text-sm text-gray-400 animate-pulse">
                  {callState === 'connecting' ? 'Connecting...' : 'Waiting for video...'}
                </p>
              </div>
            </div>
          )}

          {/* Local video (picture-in-picture) */}
          <div className="absolute bottom-4 right-4 w-24 h-32 md:top-6 md:right-6 md:w-48 md:h-64 md:bottom-auto rounded-2xl overflow-hidden shadow-2xl ring-2 ring-white/20">
            {isVideoEnabled ? (
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                <VideoOff className="h-8 w-8 md:h-16 md:w-16 text-white/50" />
              </div>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="bg-black/50 backdrop-blur-sm p-4 md:p-8 flex justify-center gap-4 md:gap-6">
          <button
            onClick={toggleMute}
            className={`${
              isMuted ? 'bg-red-500 hover:bg-red-600' : 'bg-white/10 hover:bg-white/20'
            } text-white rounded-full p-3 md:p-5 transition-all hover:scale-105 shadow-lg backdrop-blur-sm`}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? <MicOff className="h-5 w-5 md:h-7 md:w-7" /> : <Mic className="h-5 w-5 md:h-7 md:w-7" />}
          </button>

          {callData.isVideoCall && (
            <button
              onClick={toggleVideo}
              className={`${
                !isVideoEnabled ? 'bg-red-500 hover:bg-red-600' : 'bg-white/10 hover:bg-white/20'
              } text-white rounded-full p-3 md:p-5 transition-all hover:scale-105 shadow-lg backdrop-blur-sm`}
              title={isVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
            >
              {isVideoEnabled ? <Video className="h-5 w-5 md:h-7 md:w-7" /> : <VideoOff className="h-5 w-5 md:h-7 md:w-7" />}
            </button>
          )}

          <button
            onClick={handleEndCall}
            className="bg-red-500 hover:bg-red-600 text-white rounded-full p-3 md:p-5 transition-all hover:scale-105 shadow-lg"
            title="End call"
          >
            <PhoneOff className="h-5 w-5 md:h-7 md:w-7" />
          </button>
        </div>
      </div>
    );
  }

  return null;
};
